"use client";

import { isFilled } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import ReviewCard from "@/components/ReviewCard";
import "@splidejs/react-splide/css/core";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "../Review/review-slider.css";

/**
 * @typedef {import("@prismicio/client").Content.ReviewSlice} ReviewSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ReviewSlice>} ReviewProps
 * @type {import("react").FC<ReviewProps>}
 */
const Review = ({ slice }) => {
  const reviews = Array.isArray(slice.primary.reviews) ? slice.primary.reviews : [];

  const showMobileSlider = reviews.length > 0;
  const showTabletSlider = reviews.length > 3;
  const showDesktopSlider = reviews.length > 4;
  const showTabletGrid = reviews.length > 0 && reviews.length <= 3;
  const showDesktopGrid = reviews.length > 0 && reviews.length <= 4;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-bleu-900"
    >
      <div className="py-10 lg:pt-20 lg:pb-30 flex flex-col gap-16">
        {isFilled.richText(slice.primary.title) && (
          <PrismicRichText
            field={slice.primary.title}
            components={{
              heading2: ({ children }) => <h2 className="h4 text-bleu-100 padding-x framing">{children}</h2>,
              heading3: ({ children }) => <h3 className="h4 text-bleu-100 padding-x framing">{children}</h3>,
            }}
          />
        )}

        {showMobileSlider && (
          <div className="md:hidden">
            <div className="pb-8 px-3 gap-4 flex snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] overflow-x-scroll">
              {reviews.map((reviewItem, index) => (
                <ReviewCard
                  key={`mobile-review-${index}`}
                  reviewItem={reviewItem}
                  i={index}
                  className="snap-center snap-always flex-shrink-0 w-[85vw] max-w-[360px]"
                />
              ))}
            </div>
          </div>
        )}

        {showDesktopSlider && (
          <div className="hidden lg:block overflow-visible">
            <Splide
              options={{
                perPage: 4,
                perMove: 4,
                gap: "2rem",
                pagination: false,
                arrows: true,
                width: "100%",
                padding: "4rem",
              }}
              aria-label="Avis clients"
            >
              {reviews.map((reviewItem, index) => (
                <SplideSlide key={`desktop-slider-review-${index}`} className="group overflow-visible">
                  <ReviewCard reviewItem={reviewItem} i={index} className="h-full" />
                </SplideSlide>
              ))}
            </Splide>
          </div>
        )}

        {showTabletSlider && (
          <div className="hidden md:block lg:hidden overflow-visible">
            <Splide
              options={{
                perPage: 3,
                perMove: 3,
                gap: "1rem",
                pagination: false,
                arrows: true,
                width: "100%",
                padding: "1.5rem",
              }}
              aria-label="Avis clients"
            >
              {reviews.map((reviewItem, index) => (
                <SplideSlide key={`tablet-slider-review-${index}`} className="group overflow-visible">
                  <ReviewCard reviewItem={reviewItem} i={index} className="h-full" />
                </SplideSlide>
              ))}
            </Splide>
          </div>
        )}

        {showDesktopGrid && (
          <div className="hidden lg:flex flex-row justify-center gap-8 padding-x pb-8">
            {reviews.map((reviewItem, index) => (
              <ReviewCard key={`desktop-grid-review-${index}`} reviewItem={reviewItem}  className="w-1/4 " />
            ))}
          </div>
        )}

        {showTabletGrid && (
          <div className="hidden md:flex lg:hidden flex-row justify-center gap-4 padding-x pb-8">
            {reviews.map((reviewItem, index) => (
              <ReviewCard key={`tablet-grid-review-${index}`} reviewItem={reviewItem} className="w-1/3" />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Review;
